import { useLayoutEffect, useState, type RefObject } from "react"

export function fitOverflowItems({
  itemWidths,
  budget,
  moreWidth,
  pinned,
}: {
  itemWidths: number[]
  budget: number
  moreWidth: number
  pinned?: number
}): number {
  if (itemWidths.reduce((sum, w) => sum + w, 0) <= budget) return itemWidths.length

  const pinnedIndex = pinned !== undefined && pinned >= 0 ? pinned : undefined
  let used = moreWidth + (pinnedIndex !== undefined ? itemWidths[pinnedIndex]! : 0)
  let count = 0
  for (const [i, width] of itemWidths.entries()) {
    if (i === pinnedIndex) continue
    if (used + width > budget) break
    used += width
    count++
  }
  return count
}

export function isOverflowItemVisible(index: number, count: number, pinned?: number): boolean {
  if (pinned === undefined || pinned < 0) return index < count
  if (index === pinned) return true
  return (index > pinned ? index - 1 : index) < count
}

const outerWidth = (el: HTMLElement, gap: number) => el.getBoundingClientRect().width + gap

export function useOverflowItems(
  rowRef: RefObject<HTMLElement | null>,
  itemCount: number,
  pinned?: number,
): (index: number) => boolean {
  const [visibleCount, setVisibleCount] = useState<number | null>(null)

  useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return

    const measure = () => {
      const style = getComputedStyle(row)
      const gap = parseFloat(style.columnGap) || 0
      const content =
        row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) + gap
      const children = [...row.children] as HTMLElement[]
      const items = children.filter((el) => el.dataset.overflowItem !== undefined)
      const more = children.find((el) => el.dataset.overflowMore !== undefined)
      const fixed = children.filter(
        (el) => el.dataset.overflowItem === undefined && el.dataset.overflowMore === undefined,
      )
      const budget = content - fixed.reduce((sum, el) => sum + outerWidth(el, gap), 0)
      setVisibleCount(
        fitOverflowItems({
          itemWidths: items.map((el) => outerWidth(el, gap)),
          budget,
          moreWidth: more ? outerWidth(more, gap) : 0,
          pinned,
        }),
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    for (const child of row.children) observer.observe(child)
    return () => observer.disconnect()
  }, [rowRef, itemCount, pinned])

  return (index) => visibleCount === null || isOverflowItemVisible(index, visibleCount, pinned)
}
